import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { getLoggerFor } from "@tylertech/logging";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria, ProfileSettingViewModel, SettingLookupOption, } from "@tylertech/tyler-api-types";
import { ChartConfigurationCustomTypesPerDataset, ChartEvent, LegendElement, LegendItem } from "chart.js";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

const enum ParameterName
{
    ClientVendorServiceType = "TotalCommunityConsumptionVsMeterReportingChartClientVendorServiceType",
}

const enum AdditionalChartOptions
{
    DefaultSelectionForClientVendorID = "DefaultSelectionForClientVendorID"
}

@Component({
    selector: "utilities-total-community-consumption-vs-meter-reporting-chart",
    templateUrl: "./totalCommunityConsumptionVsMeterReportingChart.tmpl.html",
    styleUrls: [
        "./totalCommunityConsumptionVsMeterReportingChart.component.scss"
    ],
    standalone: false
})
export class TotalCommunityConsumptionVsMeterChartComponent implements OnInit, OnDestroy, ICustomViewComponent<ChartBindingObject>
{
    private readonly log = getLoggerFor("TotalCommunityConsumptionVsMeterChartComponent");
    private readonly destructor$ = new Subject<void>();
    private readonly serviceTypesSubject = new BehaviorSubject<Array<SettingLookupOption>>([]);
    readonly formGroup: FormGroup;


    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfigurationCustomTypesPerDataset;

    private readonly _chartId: string;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;

    isLoaded: boolean;

    constructor(
        $chartManagerService: ChartManagerService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $chartDialogService: ChartDialogService,
    )
    {
        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });

        this.formGroup = new FormGroup({
            serviceType: LgdFormControlFactory.select<SettingLookupOption>(
                undefined,
                false,
                {
                    displayValueFn: x => x.Display
                }),
        });
    }

    get chartId(): string | undefined { return this._chartId || this.customDataBinding?.chartId; }

    @Input()
    get chartCriteria(): IChartCriteria | undefined { return this._chartCriteria || this.customDataBinding?.chartCriteria; }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    private get serviceTypeControl(): AbstractControl<SettingLookupOption> { return this.formGroup.get("serviceType"); }
    private get selectedServiceType(): SettingLookupOption { return this.serviceTypeControl.value; }

    get serviceTypesObservable(): Observable<unknown[]> { return this.serviceTypesSubject.asObservable(); }

    get chartHasData(): boolean { return this.chartCriteria?.ChartDatas?.length > 0; }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        (this.chartHasData
            ? Promise.resolve()
            : this.loadChartData())
            .then(
                () =>
                {
                    const serviceTypes = this.chartCriteria.Parameters
                        .find(x => x.Id === ParameterName.ClientVendorServiceType)
                        ?.Options;
                    if (serviceTypes)
                    {
                        this.serviceTypesSubject.next(serviceTypes);
                        this.serviceTypeControl.setValue(serviceTypes[0], { emitEvent: false });
                    }

                    this.updateChartConfiguration();
                },
                e =>
                {
                    this.log.error("Error on load", e);
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        subscribeToValueChanges(this.serviceTypeControl, (_newValue: SettingLookupOption) =>
        {
            this.addServiceTypeParameterToCriteria();

            this.loadChartData();
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.chartHasData)
                    {
                        return;
                    }
                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.log.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private addServiceTypeParameterToCriteria(): void
    {
        let serviceTypeValue: ProfileSettingViewModel = this.chartCriteria?.Parameters?.find(x => x.Id === ParameterName.ClientVendorServiceType);
        if (!serviceTypeValue)
        {
            serviceTypeValue = {
                Id: ParameterName.ClientVendorServiceType
            };
            this.chartCriteria?.Parameters?.push(serviceTypeValue);
        }

        serviceTypeValue.Value = this.selectedServiceType?.Value;
    }

    private updateChartConfiguration(): void
    {
        this.addServiceTypeParameterToCriteria();
        this.displayChart();

        const vendorServiceType = this.chartCriteria.Parameters
            .find(x => x.Id === ParameterName.ClientVendorServiceType);
        const defaultServiceTypeValue = vendorServiceType?.AdditionalAttributes
            ? vendorServiceType?.AdditionalAttributes[AdditionalChartOptions.DefaultSelectionForClientVendorID] as string
            : undefined;

        if (defaultServiceTypeValue
            && this.selectedServiceType?.Value !== defaultServiceTypeValue)
        {
            const serviceType = this.serviceTypesSubject.value.find(x => x.Value === defaultServiceTypeValue);
            this.serviceTypeControl.setValue(serviceType, { emitEvent: false });
        }
    }

    private displayChart(): void
    {
        this.chartConfig = {
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12, color: "#333333" },
                        onClick: (_event: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = !meta.hidden ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (!iMeta.hidden && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                    tooltip: {
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    }
                },
                elements: { line: {} },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(
                                    value > -10 && value < 10
                                        ? value.toFixed(2)
                                        : value.toFixed(0));
                            },
                        },
                    },
                    barAxis: {
                        type: "linear",
                        display: true,
                        position: "right",
                        grid: { display: false },
                        beginAtZero: true,
                        ticks: {
                            callback: (value: number) => value,
                        },
                    },
                },
            },
            data: {
                datasets: this.chartCriteria.ChartDatas.length === 2
                    ? [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[0].Data,
                            backgroundColor: this.chartCriteria.ChartDatas[0].Additional[0],
                            borderColor: this.chartCriteria.ChartDatas[0].Additional[0],
                            hoverBackgroundColor: this.chartCriteria.ChartDatas[0].Additional[0],
                            hoverBorderColor: this.chartCriteria.ChartDatas[0].Additional[0],
                            yAxisID: "yAxis",
                            categoryPercentage: 0.5,
                            barPercentage: 0.8,
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            borderColor: "#FF6E40",
                            backgroundColor: "#FF6E40",
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointBackgroundColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "#FF6E40",
                            pointHoverBorderColor: "#FF6E40",
                            yAxisID: "barAxis",
                        }
                    ]
                    : [],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }

    displayChartParameterDialogAndReload(): void
    {
        const parameters = Utilities.deepCopy(this.chartCriteria.Parameters);
        this.$chartDialogService.show(parameters, "Total Meters vs Meters Reporting")
            .then((result: ProfileSettingViewModel[]) =>
            {
                if (Utilities.arraysEqual(this.chartCriteria.Parameters, result))
                {
                    this.log.debug(`Chart Parameters didn't change`);
                    return;
                }

                this.chartCriteria.Parameters = result;
                return this.loadChartData();
            });
    }
}
